import { defineMessages, useIntl } from 'react-intl';

import type { ComponentProps } from 'react';
import cx from 'classnames';
import config from '@plone/registry';
import MaybeWrap from '@plone/volto/components/manage/MaybeWrap/MaybeWrap';
import { TextBlockView } from '@plone/volto-slate/blocks/Text';
import type { BlockViewProps } from '@plone/types';

import Slides from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/Slides';
import styles from '@redturtle/qubicaamf-theme/components/Blocks/ImageGallery/styles.module.css';
import type { ImageGalleryData } from './schema';
import CTA from '../commons/CTA';

type ImageGalleryViewProps = Omit<BlockViewProps, 'data'> & {
  data: ImageGalleryData;
};

const View = (props: ImageGalleryViewProps) => {
  const intl = useIntl();
  const { className, style, data } = props;
  const Container = config.getComponent('Container').component || 'div';
  const Wrapper = (props: ComponentProps<any>) => (
    <Container
      {...props}
      className={cx('block-imagegallery-container', styles.container)}
    />
  );

  return (
    <section
      className={cx('block-imagegallery', styles.block, className)}
      style={style}
      aria-label={data.title}
    >
      {/* @ts-expect-error */}
      <MaybeWrap condition={!data?.styles?.fullwidth} as={Wrapper}>
        {data.title && (
          <div className={styles.titleContainer}>
            <h2 className={cx('block-imagegallery-title', styles.title)}>
              {data.title}
            </h2>
          </div>
        )}
        {data.text && (
          <div className={cx('block-imagegallery-text', styles.description)}>
            <TextBlockView data={{ value: data.text ?? {} }} />
          </div>
        )}
        {data?.slides?.length > 0 ? (
          <Slides slides={data.slides} editMode={false} />
        ) : (
          <div className={styles.placeholder}>
            {intl.formatMessage(messages.help_text)}
          </div>
        )}
        {data.linkHref?.[0] && (
          <div className={styles.ctaContainer}>
            <CTA
              href={data.linkHref[0]['@id']}
              openLinkInNewTab={false}
              ctaButtonStyle={data.ctaButtonStyle}
              linkIcon={data.linkIcon}
              cta_color={data.cta_color}
            >
              {data.linkTitle}
            </CTA>
          </div>
        )}
      </MaybeWrap>
    </section>
  );
};

export default View;

const messages = defineMessages({
  help_text: {
    id: 'help_text',
    defaultMessage: 'Add slides from the sidebar',
  },
});
